import { useCallback, useEffect, useState } from 'react';
import AxiosFactory from '../../services/http.service';
import ApiRoutes from '../../config/api.routes';
import useMetaMask from '../../contexts/useMetaMask';
import { DocumentType, IpfsDocument, SigningMethod, TemplatePlaceholders, isAlwaysWetInk } from './ipfsDocumentTypes';

export interface UseIpfsDocumentCreatorOptions {
  /** Pre-selected document type. Defaults to `MASTER_ARCHITECTURE`. Stays editable. */
  initialType?: DocumentType;
  /** Additional signers to pre-fill (excluding the creator, who is always signer 1). */
  initialSigners?: string[];
  /** Pre-filled template variable values, keyed by placeholder token (e.g. `TOKEN_ID`). */
  initialVars?: Record<string, string>;
}

/**
 * Drives the admin "create document" form: picks a type, loads its placeholders so initial values
 * can be supplied, collects required signers, and POSTs the new document. On success it exposes
 * `createdDocId` so the caller can hand off to the signer view.
 *
 * The connected wallet is always the first required signer and cannot be edited or removed —
 * `signers` holds only the additional signers the creator adds.
 * See IPFS_DOCUMENT_MODULE.md §5 (POST /ipfs-document).
 */
export function useIpfsDocumentCreator(options: UseIpfsDocumentCreatorOptions = {}) {
  const { initialType = DocumentType.MASTER_ARCHITECTURE, initialSigners = [], initialVars = {} } = options;
  const { currentAccount } = useMetaMask();

  const [type, setType] = useState<DocumentType>(initialType);
  // Some types (covenants/deeds) are always wet-ink: no wallet signing, and the creator is not
  // auto-added as a signer — every listed signer is a required wet-ink signer.
  const alwaysWetInk = isAlwaysWetInk(type);
  const [placeholders, setPlaceholders] = useState<string[]>([]);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);
  const [placeholdersError, setPlaceholdersError] = useState<string | null>(null);

  const [varDrafts, setVarDrafts] = useState<Record<string, string>>(initialVars);
  // Additional signers only — the creator (currentAccount) is implicitly signer 1 and always
  // WALLET (they must sign themselves). `signerMethods` is a parallel array to `signers`.
  const [signers, setSigners] = useState<string[]>(initialSigners);
  const [signerMethods, setSignerMethods] = useState<SigningMethod[]>(
    initialSigners.map(() => SigningMethod.WALLET)
  );

  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdDocId, setCreatedDocId] = useState<number | null>(null);

  // Reload placeholders whenever the document type changes.
  useEffect(() => {
    let active = true;
    setLoadingPlaceholders(true);
    setPlaceholdersError(null);

    const { axios } = new AxiosFactory();
    axios
      .get<TemplatePlaceholders>(ApiRoutes.ipfsDocument.templates(type))
      .then((res) => {
        if (active) {setPlaceholders(res.data.placeholders ?? []);}
      })
      .catch((err: any) => {
        if (active) {
          setPlaceholders([]);
          setPlaceholdersError(
            err?.response?.data?.message || err?.message || 'Failed to load template fields.'
          );
        }
      })
      .finally(() => {
        if (active) {setLoadingPlaceholders(false);}
      });

    return () => {
      active = false;
    };
  }, [type]);

  const setVarDraft = useCallback((key: string, value: string) => {
    setVarDrafts((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setSigner = useCallback((index: number, value: string) => {
    setSigners((prev) => prev.map((s, i) => (i === index ? value : s)));
  }, []);

  const setSignerMethod = useCallback((index: number, method: SigningMethod) => {
    setSignerMethods((prev) => prev.map((m, i) => (i === index ? method : m)));
  }, []);

  const addSigner = useCallback(() => {
    setSigners((prev) => [...prev, '']);
    setSignerMethods((prev) => [...prev, SigningMethod.WALLET]);
  }, []);

  const removeSigner = useCallback((index: number) => {
    setSigners((prev) => prev.filter((_, i) => i !== index));
    setSignerMethods((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleCreate = useCallback(async () => {
    setCreateLoading(true);
    setCreateError(null);
    try {
      const requiredSigners: string[] = [];
      const signerMethodMap: Record<string, string> = {};

      if (alwaysWetInk) {
        // Always-wet-ink types: the creator is NOT auto-added. Every listed signer is a required
        // signer, and every one is forced to WET_INK regardless of the (hidden) method selector.
        const seen = new Set<string>();
        signers.forEach((s) => {
          const wallet = s.trim();
          const key = wallet.toLowerCase();
          if (wallet && !seen.has(key)) {
            seen.add(key);
            requiredSigners.push(wallet);
            signerMethodMap[wallet] = SigningMethod.WET_INK;
          }
        });
        if (requiredSigners.length === 0) {
          setCreateError('Add at least one wet-ink signer.');
          return;
        }
      } else {
        const creator = currentAccount.trim();
        if (!creator) {
          setCreateError('Connect your wallet to create a document.');
          return;
        }
        // Creator is always signer 1. Append the extra signers, dropping blanks and any duplicate of
        // the creator or another signer (case-insensitive). Track each kept signer's method so the
        // `signerMethods` map lines up with the wallets that actually made it into requiredSigners.
        const seen = new Set<string>([creator.toLowerCase()]);
        requiredSigners.push(creator);
        signers.forEach((s, i) => {
          const wallet = s.trim();
          const key = wallet.toLowerCase();
          if (wallet && !seen.has(key)) {
            seen.add(key);
            requiredSigners.push(wallet);
            // Only non-default (WET_INK) entries need to be sent — omitted wallets default to WALLET.
            if (signerMethods[i] === SigningMethod.WET_INK) {
              signerMethodMap[wallet] = SigningMethod.WET_INK;
            }
          }
        });
      }

      const templateVars: Record<string, string> = {};
      placeholders.forEach((key) => {
        const value = (varDrafts[key] ?? '').trim();
        if (value) {templateVars[key] = value;}
      });

      const { axios } = new AxiosFactory();
      const res = await axios.post<IpfsDocument>(ApiRoutes.ipfsDocument.create, {
        type,
        templateVars,
        requiredSigners,
        ...(Object.keys(signerMethodMap).length > 0 ? { signerMethods: signerMethodMap } : {}),
      });
      setCreatedDocId(res.data.id);
    } catch (e: any) {
      setCreateError(e?.response?.data?.message || e?.message || 'Failed to create document.');
    } finally {
      setCreateLoading(false);
    }
  }, [alwaysWetInk, currentAccount, signers, signerMethods, placeholders, varDrafts, type]);

  return {
    type,
    setType,
    alwaysWetInk,
    placeholders,
    loadingPlaceholders,
    placeholdersError,
    varDrafts,
    setVarDraft,
    creatorAddress: currentAccount,
    signers,
    signerMethods,
    setSigner,
    setSignerMethod,
    addSigner,
    removeSigner,
    createLoading,
    createError,
    createdDocId,
    handleCreate,
  };
}
